import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { NavLink, useNavigate } from "react-router-dom";
import { useTenant } from "../hooks/useTenant";
import { getAllowedMenuIds } from "../config/tenants";
import {
  MessageSquare,
  Search,
  History,
  LogOut,
  Bookmark,
  Briefcase,
  Bell,
  BookOpen,
  X,
  PanelLeftClose,
  PanelLeftOpen,
  SquarePen,
  Trash2,
  Home,
} from "lucide-react";
import { searchStocks, stockPath } from "../lib/stockApi";
import { listChats, createChat, deleteChat, refreshChats, getCreds, pruneEmptyChats, subscribe as subscribeHermesChats } from "../lib/hermesChats";

// Laid out like the desktop app's sidebar (itself modelled on t3code): a
// fixed top with search and new chat, a scrolling chat list, and the account
// row at the bottom, which opens Settings.

const menuItems = [
  { id: "home",      path: "/",          label: "Home",      icon: Home },
  { id: "chat",      path: "/chat",      label: "Chat",      icon: MessageSquare },
  { id: "screener",  path: "/screener",  label: "Screener",  icon: Search },
  { id: "history",   path: "/history",   label: "History",   icon: History },
  { id: "watchlist", path: "/watchlist", label: "Watchlist", icon: Bookmark },
  { id: "portfolio", path: "/portfolio", label: "Portfolio", icon: Briefcase },
  { id: "alerts",    path: "/alerts",    label: "Alerts",    icon: Bell },
  { id: "blog",      path: "/blog",      label: "Blog",      icon: BookOpen },
];

// Desired order; anything unlisted goes last.
const ORDER = ["home", "chart", "watchlist", "portfolio", "alerts", "blog"];

// Desktop palette (analystbro-desktop/src/App.css).
const C = {
  bg: "bg-[#12161d]",
  line: "border-[#252b36]",
  hover: "hover:bg-[#182029]",
  active: "bg-[#1d2634] border-[#2b3547]",
  fg: "text-[#e6e9ef]",
  dim: "text-[#8b94a5]",
};

/** "now", "5m", "3h", "2d", or a short date — the row's right-hand label. */
function shortAge(ms) {
  if (!ms) return "";
  const s = Math.max(0, (Date.now() - ms) / 1000);
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)}d`;
  return new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function useChats(userId) {
  const [chats, setChats] = useState(() => listChats(userId));
  useEffect(() => {
    pruneEmptyChats(userId);
    setChats(listChats(userId));
    return subscribeHermesChats(() => setChats(listChats(userId)));
  }, [userId]);

  // Self-populate from the server using cached creds, on mount and whenever
  // the tab regains focus. A brand-new device with no creds stays empty until
  // the first chat visit caches them.
  useEffect(() => {
    const pull = () => {
      const creds = getCreds();
      if (creds) refreshChats(userId, creds.baseUrl, creds.token).catch(() => { /* transient */ });
    };
    pull();
    const onFocus = () => { if (document.visibilityState === "visible") pull(); };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
  }, [userId]);
  return chats;
}

function NavItem({ item, collapsed, onNavigate }) {
  const { path, label, icon: Icon } = item;
  return (
    <NavLink
      to={path}
      end={path === "/"}
      onClick={onNavigate}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        `flex items-center rounded-lg border text-[13px] transition-colors
        ${collapsed ? "justify-center h-8 w-8 mx-auto" : "gap-2.5 px-2 py-1.5"}
        ${isActive ? `${C.active} ${C.fg}` : `border-transparent ${C.dim} ${C.hover} hover:text-[#e6e9ef]`}`
      }
    >
      <Icon size={15} className="flex-shrink-0" />
      {!collapsed && label}
    </NavLink>
  );
}

export default function Sidebar({ isOpen, onClose }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebar_collapsed") === "true");
  const [query, setQuery] = useState("");
  const { subdomain } = useTenant();
  const allowedIds = getAllowedMenuIds(subdomain);
  const navItems = menuItems
    .filter((item) => allowedIds.includes(item.id))
    .sort((a, b) => {
      const ia = ORDER.indexOf(a.id), ib = ORDER.indexOf(b.id);
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });

  const chats = useChats(user?.id);
  const q = query.trim().toLowerCase();
  const [stockHits, setStockHits] = useState([]);
  useEffect(() => {
    const term = query.trim();
    if (!term) {
      setStockHits([]);
      return;
    }
    let live = true;
    const t = setTimeout(() => {
      searchStocks(term)
        .then((d) => live && setStockHits((d.results || []).slice(0, 5)))
        .catch(() => live && setStockHits([]));
    }, 200);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [query]);
  const shown = q ? chats.filter((c) => (c.title || "").toLowerCase().includes(q)) : chats;

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      localStorage.setItem("sidebar_collapsed", String(!prev));
      return !prev;
    });
  };

  const newChat = () => {
    const c = createChat(user?.id);
    onClose?.();
    navigate(`/agentbro/${c.id}`);
  };

  const iconBtn = `flex items-center justify-center h-8 w-8 rounded-lg border border-transparent ${C.dim} hover:text-[#e6e9ef] hover:border-[#252b36] transition-colors`;

  return (
    <aside
      className={`
        fixed inset-y-0 left-0 z-50 flex flex-col ${C.bg} border-r ${C.line} overflow-hidden
        transition-all duration-200 ease-in-out
        ${isOpen ? "translate-x-0" : "-translate-x-full"}
        ${collapsed ? "w-14" : "w-60"}
        md:relative md:translate-x-0 md:z-auto md:flex-shrink-0 md:h-screen
      `}
    >
      {/* Top: collapse toggle + name; search + new chat */}
      <div className={`flex-shrink-0 ${collapsed ? "px-2 pt-2.5" : "px-2.5 pt-2.5"} space-y-2`}>
        <div className={`flex items-center ${collapsed ? "flex-col gap-1" : "gap-1.5"}`}>
          <button
            onClick={toggleCollapsed}
            className={`hidden md:flex ${iconBtn}`}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
          </button>
          {!collapsed && <span className={`font-semibold text-[15px] tracking-tight ${C.fg} flex-1`}>AnalystBro</span>}
          <button onClick={onClose} className={`md:hidden ${iconBtn}`} aria-label="Close menu">
            <X size={16} />
          </button>
          {collapsed && (
            <button onClick={newChat} className={iconBtn} title="New chat">
              <SquarePen size={16} />
            </button>
          )}
        </div>
        {!collapsed && (
          <div className="flex items-center gap-1.5">
            <div className={`flex-1 min-w-0 flex items-center gap-2 rounded-lg border ${C.line} px-2 py-1.5 ${C.dim} focus-within:border-[#4f9cf9]`}>
              <Search size={14} className="flex-shrink-0" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search chats & symbols"
                aria-label="Search chats and symbols"
                className={`flex-1 min-w-0 bg-transparent outline-none border-none text-[12px] ${C.fg} placeholder:text-[#8b94a5]`}
              />
              {query && (
                <button onClick={() => setQuery("")} aria-label="Clear search" className="hover:text-[#e6e9ef]">
                  <X size={12} />
                </button>
              )}
            </div>
            <button onClick={newChat} className={iconBtn} title="New chat">
              <SquarePen size={16} />
            </button>
          </div>
        )}

        {/* One search box: matching stocks (open /symbols/EXCHANGE:SYMBOL) above matching chats. */}
        {!collapsed && q && stockHits.length > 0 && (
          <div className="pb-2">
            <div className={`px-2 pb-1 text-[11px] uppercase tracking-wide ${C.dim}`}>Stocks</div>
            {stockHits.map((r) => (
              <button
                key={r.exsym}
                onClick={() => { setQuery(""); onClose?.(); navigate(stockPath(r.exsym)); }}
                className={`w-full text-left flex items-baseline gap-2 rounded-lg px-2 py-1.5 text-[12px] ${C.fg} hover:bg-white/5`}
              >
                <span className="font-mono font-semibold shrink-0">{r.exsym}</span>
                <span className={`truncate ${C.dim}`}>{r.name}</span>
              </button>
            ))}
          </div>
        )}

        {/* Pages */}
        <nav className="space-y-0.5 pb-2">
          {navItems.map((item) => (
            <NavItem key={item.id} item={item} collapsed={collapsed} onNavigate={onClose} />
          ))}
        </nav>
      </div>

      {/* Chats: only this part scrolls */}
      <div className={`flex-1 min-h-0 overflow-y-auto border-t ${C.line} ${collapsed ? "hidden" : "px-2.5 pt-2"}`}>
        <div className={`px-2 pb-1 text-[11px] font-medium ${C.dim}`}>Chats</div>
        {shown.length === 0 ? (
          <div className={`text-center text-[12px] ${C.dim} mt-4`}>{q ? "No matching chats" : "No chats yet"}</div>
        ) : (
          shown.map((c) => (
            <NavLink
              key={c.id}
              to={`/agentbro/${c.id}`}
              onClick={onClose}
              title={c.title}
              className={({ isActive }) =>
                `group flex items-center gap-2 rounded-lg border px-2 py-1.5 mb-0.5 text-[13px] transition-colors
                ${isActive ? `${C.active} ${C.fg}` : `border-transparent ${C.fg} ${C.hover}`}`
              }
            >
              <span className="truncate flex-1">{c.title}</span>
              <span className={`text-[10px] ${C.dim} group-hover:hidden`}>{shortAge(c.updatedAt)}</span>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (confirm("Remove this chat from the list?")) deleteChat(user?.id, c.id);
                }}
                title="Remove from list"
                className={`hidden group-hover:block ${C.dim} hover:text-[#f85149]`}
              >
                <Trash2 size={12} />
              </button>
            </NavLink>
          ))
        )}
      </div>
      {collapsed && <div className="flex-1" />}

      {/* Account row: opens Settings */}
      <div className={`flex-shrink-0 border-t ${C.line} ${collapsed ? "px-2 py-2" : "px-2.5 py-2"}`}>
        <div className={`flex items-center ${collapsed ? "flex-col gap-1" : "gap-1"}`}>
          <button
            type="button"
            onClick={() => { onClose?.(); navigate("/settings"); }}
            title="Settings"
            className={`flex items-center min-w-0 rounded-lg ${C.hover} transition-colors text-left ${collapsed ? "p-1" : "flex-1 gap-2 px-1.5 py-1"}`}
          >
            <span className="w-6 h-6 rounded-full bg-[#1d2634] text-[#4f9cf9] flex items-center justify-center flex-shrink-0 text-[11px] font-semibold">
              {(user?.first_name?.[0] || user?.email?.[0] || "U").toUpperCase()}
            </span>
            {!collapsed && (
              <span className="flex-1 min-w-0 flex flex-col">
                <span className={`text-[13px] ${C.fg} truncate`}>{user?.first_name || "User"}</span>
                <span className={`text-[11px] ${C.dim} truncate`}>{user?.email}</span>
              </span>
            )}
          </button>
          <button onClick={logout} className={`${iconBtn} hover:!text-[#f85149]`} title="Log out">
            <LogOut size={15} />
          </button>
        </div>
      </div>
    </aside>
  );
}
